import axios from 'axios';

// Remembers which build we already force-reloaded for. If the reload comes back on the same old
// bundle (CloudFront still serving the previous index.html), we must not reload again or the page
// loops until the edge cache expires. sessionStorage is per tab, which is the right scope.
const RELOADED_FOR_KEY = 'appUpdateReloadedFor';

class AppUpdateService {
  static DELAY = 60000; // 1 minute

  currentETag = null;
  availableETag = null;
  intervalID = null;
  currentBuildVersion = null;
  availableBuildVersion = null;

  enable() {
    this.disable(); // Prevent multiple intervals
    this.intervalID = setInterval(this.checkForUpdate, AppUpdateService.DELAY);
    if (process.env.REACT_APP_ENVIRONMENT !== 'local') {
      this.fetchAvailableBuildVersion();
    }
  }

  disable() {
    if (this.intervalID) clearInterval(this.intervalID);
  }

  isUpdateAvailable = () => (this.currentETag !== this.availableETag ||
       (this.availableBuildVersion && this.availableBuildVersion > this.currentBuildVersion)
  )

  // Identity of the update we would reload for, used to dedupe reloads across page loads.
  updateSignature = () => `${this.availableBuildVersion || ''}:${this.availableETag || ''}`;

  /**
   * True if we should force a reload now. Returns false (and lets navigation proceed) when a
   * reload for this same update already happened in this tab, so a stale edge cache cannot
   * turn the update check into a reload loop. A later update (new signature) reloads again.
   */
  shouldReloadForUpdate = () => {
    if (!this.isUpdateAvailable()) return false;
    const signature = this.updateSignature();
    let alreadyReloadedFor = null;
    try { alreadyReloadedFor = sessionStorage.getItem(RELOADED_FOR_KEY); } catch (e) { /* storage unavailable */ }
    if (alreadyReloadedFor === signature) {
      console.warn('App update detected but the page already reloaded for it; the CDN is likely still serving the previous index.html. Continuing without reload.'); // eslint-disable-line no-console
      return false;
    }
    try { sessionStorage.setItem(RELOADED_FOR_KEY, signature); } catch (e) { /* storage unavailable */ }
    return true;
  }

  fetchAvailableBuildVersion() {
    fetch('/meta.json')
      .then((response) => response.json())
      .then((meta) => {
        this.currentBuildVersion = process.env.BUILD_VERSION ? parseInt(process.env.BUILD_VERSION, 10) : null;
        this.availableBuildVersion = meta.BUILD_VERSION ? parseInt(meta.BUILD_VERSION, 10) : null;
        console.log(`Current build version: ${this.currentBuildVersion}`, `Available build version: ${this.availableBuildVersion}`); // eslint-disable-line no-console
      }).catch((e) => {
        console.error('Error fetching build version: ', e);
      });
  }

  fetchETag = () =>
    axios.head('/', {
      baseURL: `${window.location.protocol}//${window.location.host}`,
    })
      .then((response) => response.headers.etag);

  updateETags = (etag) => {
    if (!this.currentETag) this.currentETag = etag;
    this.availableETag = etag;
  }

  checkForUpdate = () => {
    this.fetchETag()
      .then(this.updateETags)
      .catch((e) => {
        console.error('Error checking for app update: ', e);
      });
  }
}

export default new AppUpdateService();
